import { Effect, Schedule } from "effect";
import { status } from "http-status";
import { Duration } from "luxon";

const DEFAULT_RETRY_DELAY_MS = Duration.fromObject({ seconds: 1 }).toMillis();

const NON_RETRYABLE_STATUSES: ReadonlySet<number> = new Set([
  status.BAD_REQUEST,
  status.UNAUTHORIZED,
  status.FORBIDDEN,
  status.NOT_FOUND,
  status.UNPROCESSABLE_ENTITY,
]);

const DEFAULT_RETRIES = 3;

export interface RetryWithEffectOptions {
  delayMs?: number;
  retries?: number;
  while?: (error: unknown) => boolean;
}

export const isRetryableFetchError = (error: unknown): boolean => {
  if (typeof error !== "object" || error === null) {
    return true;
  }

  if (!("status" in error) || typeof error.status !== "number") {
    return true;
  }

  return !NON_RETRYABLE_STATUSES.has(error.status);
};

export const retryEffect = <T>(
  operation: () => Promise<T>,
  {
    delayMs = DEFAULT_RETRY_DELAY_MS,
    retries = DEFAULT_RETRIES,
    while: shouldRetry = isRetryableFetchError,
  }: RetryWithEffectOptions = {}
): Effect.Effect<T, Error> => {
  const operationEffect = Effect.tryPromise({
    catch: (error: unknown) => error as Error,
    try: operation,
  });

  const retrySchedule = Schedule.exponential(delayMs, 2).pipe(
    Schedule.compose(Schedule.recurs(retries))
  );

  return Effect.retry(operationEffect, {
    schedule: retrySchedule,
    while: shouldRetry,
  });
};
